import BackendLayout from "pages/backend/BackendLayout";
import FrontendLayout from "pages/frontend/FrontendLayout";
import { isHasAccess } from "roles_management/rolesHandler";
import { getRoute } from "./routeManagement";

/**
 * A function to handle route rendering based on security and user role.
 * @param {object} options - route configuration options.
 * @param {string} [options.path] - path for the route.
 * @param {Element.jsx} [options.element] - component to render at that path.
 * @param {boolean} [options.secure] - is the path is secure.
 * @param {boolean} [options.isFrontendLayout] - layout in which component should render.
 * @param {object | {}} [options.allowedRoles] - allowed roles for this path.
 * @param {number | null} [options.userRole] - role of the user.
 * @returns {object | {}}
 */
export const RouteHandler = (options) => {
    try {
        const {
            path = '/',
            element = <></>,
            secure = false,
            isFrontendLayout = false,
            allowedRoles = {},
            userRole = null
        } = options;
        if (!path || !element) {
            throw new Error(`'path' and 'element' are required`);
        }
        const component = isFrontendLayout
            ? <FrontendLayout children={element} secure={secure} bypassAuthCheck={!secure} />
            : <BackendLayout children={element} secure={secure} />;
        if (!secure) {
            return getRoute({ path, element: component });
        }
        if (isHasAccess(allowedRoles, userRole)) {
            return getRoute({
                path,
                element: component,
            });
        }
        return {};
    } catch (err) {
        console.error(`---Error: Error while handling routes: ${err}`);
        return {};
    }
}

/**
 * This function is responsible for genereating routes for specific module
 * @param {object} options - module configuration options.
 * @param {boolean} [options.hasWrapper] - has wrapper.
 * @param {func} [options.moduleWrapper] - function for wrapping your routes in wrapper component.
 * @param {boolean} [options.isSecureModule] - is the module secure.
 * @param {object} [options.moduleAllowedRoles] - allowed roles for this module.
 * @param {number | null} [options.userRole] - role of the user.
 * @param {func} [options.moduleLayout] - layout in which component should render.
 * @param {boolean} [options.modulePrefix] - module path prefix.
 * @param {Array} [routes] - array of routes object.
 * @returns {object | []}
 */
export const generateSpecificModuleRoutes = (options = {}, routes = []) => {
    const {
        hasWrapper = false,
        moduleWrapper = (children) => children,
        modulePrefix = '/portal',
        moduleAllowedRoles = {},
        userRole = null,
        moduleLayout = isFrontendLayout('FrontendLayout'),
        isSecureModule = true,
    } = options;
    const generatedRoutes = routes.map(route => {
        if (!route.path) {
            throw new Error('Error generating module routes - invalid path: ' + route.path);
        }
        if (!route.element) {
            throw new Error('Error generating module routes - invalid element: ' + route.element);
        }
        return RouteHandler({
            path: `${modulePrefix}${route.path}`,
            element:  hasWrapper ? moduleWrapper(route.element) : route.element,
            secure: route.secure ? route.secure : isSecureModule,
            isFrontendLayout: route.isFrontendLayout ? route.isFrontendLayout : moduleLayout,
            allowedRoles: route.allowedRoles ? route.allowedRoles : moduleAllowedRoles,
            userRole: route.userRole ? route.userRole : userRole,
        });
    });
    return generatedRoutes;
}

/** * This function checks if the layout is FrontendLayout.
 * @param {string} [layout] - layout name.
 * @returns {boolean}
 */
export const isFrontendLayout = (layout = 'FrontendLayout') => {
    return layout === 'FrontendLayout';
}